import { useSetDocumentTitle } from 'hooks/useSetDocumentTitle';
import type { CmsApiSearchLandingPage } from 'modules/cms/api/types/CmsApiSearchLandingPage';
import { SearchBody } from 'modules/search/components/Body/SearchBody';
import { SearchResultsHeader } from 'modules/search/components/Results/Header/SearchResultsHeader';
import { SearchResultsHits } from 'modules/search/components/Results/Hits/SearchResultsHits';
import { SearchSidebar } from 'modules/search/components/Sidebar/SearchSidebar';
import { searchFacetsByType } from 'modules/search/constants/searchFacetsByType';
import { useSaveSearch } from 'modules/search/hooks/useSaveSearch';
import { useSearchAnalytics } from 'modules/search/hooks/useSearchAnalytics';
import { useSearchPageActions } from 'modules/search/hooks/useSearchPageActions';
import {
  useIsSearching,
  useSearchCurrentPageResults,
  useSearchLocation,
  useSearchQuery,
  useSearchSort,
} from 'modules/search/zustand-stores/searchStore';

import { SearchDescriptionBody } from './SearchDescriptionBody';
import { useSearchLandingPageLifecycle } from './useSearchLandingPageLifecycle';

type Props = {
  content: CmsApiSearchLandingPage;
  isSsr: boolean;
};

export function SearchLandingPage({ content, isSsr }: Props) {
  const results = useSearchCurrentPageResults();

  const searchType = content.filters.type;
  const searchFacets = searchFacetsByType[searchType];

  const query = useSearchQuery();
  const isSearching = useIsSearching();
  const searchLocation = useSearchLocation();
  const sort = useSearchSort();

  useSetDocumentTitle(
    content.location?.name
      ? `${content.name}, ${content.location.name}`
      : content.name,
  );

  const { saveSearch, saveableSearch } = useSaveSearch({ searchFacets });
  const { setSearchUrlPageIndex, setSearchUrlSort } = useSearchPageActions({
    isInSearchLandingPage: true,
  });

  const { getSearchTrackingData } = useSearchAnalytics();
  const { initialized } = useSearchLandingPageLifecycle({
    content,
    isSsr,
    getSearchTrackingData,
    searchFacets,
  });

  // @TODO - Remove when `sidebar` no longer exists as prop of `SearchBody`
  const noSidebar = true;

  return (
    <SearchBody
      sidebar={
        noSidebar ? null : (
          <SearchSidebar
            initialized={initialized}
            isInSearchLandingPage
            searchFacets={searchFacets}
            allowAds={searchType === 'JOB'}
          />
        )
      }
    >
      <SearchDescriptionBody content={content} />

      <SearchResultsHeader
        results={results}
        isSearching={isSearching}
        query={query}
        sort={sort}
        searchType={searchType}
        saveSearch={saveSearch}
        saveableSearch={saveableSearch}
        onChangeSort={setSearchUrlSort}
        searchFacets={searchFacets}
      />

      <SearchResultsHits
        results={results}
        isSearching={isSearching}
        searchType={searchType}
        searchLocation={searchLocation}
        saveSearch={saveSearch}
        saveableSearch={saveableSearch}
        getSearchTrackingData={getSearchTrackingData}
        onChangePageIndex={setSearchUrlPageIndex}
        searchFacets={searchFacets}
      />
    </SearchBody>
  );
}
